import { ChunkUpdateRecoveryOverlay } from '@features/PWA/ChunkUpdateRecoveryOverlay';
import {
  isAppServiceWorkerScript,
  shouldUseServiceWorker,
  unregisterDevelopmentAppServiceWorkers,
} from '@shared/utils/serviceWorker';
import { type ReactNode, useEffect, useState } from 'react';

import { ChunkErrorBoundary } from './ChunkErrorBoundary.tsx';

interface AppWrapperProps {
  children: ReactNode;
}

/**
 * AppWrapper component that initializes global error handlers for chunk loading failures.
 * This wraps the entire app and ensures chunk errors are caught and handled properly.
 */
export const AppWrapper = ({ children }: AppWrapperProps) => {
  const [needsDevServiceWorkerRefresh, setNeedsDevServiceWorkerRefresh] = useState(false);

  // Disable only this app's old root-scoped worker in development. The current
  // controller remains attached until the developer chooses to refresh.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    if (!shouldUseServiceWorker()) {
      const appWorkerControlsTab = isAppServiceWorkerScript(
        navigator.serviceWorker.controller?.scriptURL,
      );
      if (appWorkerControlsTab) setNeedsDevServiceWorkerRefresh(true);

      void unregisterDevelopmentAppServiceWorkers(
        navigator.serviceWorker,
        window.location.origin,
      ).catch(error => console.warn('Could not unregister the local app service worker:', error));
      return;
    }

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log('Service Worker updated to version:', event.data.version);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    // Check for production service worker updates periodically.
    const updateInterval = setInterval(() => {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update().catch(error => {
            console.error('Error checking for service worker updates:', error);
          });
        });
      });
    }, 60000);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      clearInterval(updateInterval);
    };
  }, []);

  return (
    <>
      {needsDevServiceWorkerRefresh && (
        <div
          className="fixed inset-x-0 top-0 z-[10000] flex items-center justify-between gap-3 bg-warning px-4 py-3 text-sm text-warning-content shadow-lg"
          role="status"
        >
          <span>
            A previous service worker still controls this development tab. Save any open form, then
            refresh to load the live dev server.
          </span>
          <button className="btn btn-sm" onClick={() => window.location.reload()} type="button">
            Refresh after saving
          </button>
        </div>
      )}
      <ChunkErrorBoundary>{children}</ChunkErrorBoundary>
      <ChunkUpdateRecoveryOverlay />
    </>
  );
};
